import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { api, apiError } from "@/lib/api";
import { BellRing, Loader2, FileDown, Send, Trash2, Search, MapPin } from "lucide-react";

const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

export default function AdminWaitlist() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const r = await api.get("/admin/booking/waitlist", { params });
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/booking/waitlist/${id}/notify`);
      toast.success("Email de notification envoyé.");
      await load();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée de la liste d'attente ?")) return;
    setBusy(id);
    try {
      await api.delete(`/admin/booking/waitlist/${id}`);
      await load();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/booking/waitlist/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "liste_attente.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  const counts = data.counts || {};

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-extrabold tracking-tighter">
              <BellRing className="h-7 w-7 text-brand" /> {"Liste d'attente"}
            </h1>
            <p className="mt-2 text-neutral-500">
              {"Élèves à prévenir dès qu'une auto-école ouvre des places en accéléré dans leur rayon. Les notifications partent automatiquement (une seule fois par élève). La demande par zone ci-dessous t'aide à démarcher de nouvelles auto-écoles là où les élèves attendent."}
            </p>
          </div>
          <button
            onClick={exportCsv}
            data-testid="waitlist-export"
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold hover:border-brand"
          >
            <FileDown className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            {[
              { v: "", l: `Toutes (${counts.total || 0})` },
              { v: "active", l: `En attente (${counts.active || 0})` },
              { v: "notified", l: `Notifiées (${counts.notified || 0})` },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setStatus(t.v)}
                data-testid={`waitlist-filter-${t.v || "all"}`}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                  status === t.v ? "bg-brand text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                }`}
              >
                {t.l}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (email, ville…)"
              data-testid="waitlist-search"
              className="w-64 rounded-lg border border-neutral-200 py-2 pl-9 pr-3 text-sm"
            />
          </div>
        </div>

        {(data.zones || []).length > 0 && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="waitlist-zones">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-amber-600" />
              <h2 className="text-sm font-bold text-amber-800">Demande à couvrir — prospection auto-écoles</h2>
            </div>
            <p className="mt-1 text-xs text-amber-700/80">
              {"Zones avec le plus d'élèves en attente (statut « En attente »). Idéal pour signer de nouvelles auto-écoles."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.zones.map((z) => (
                <button
                  key={z.city_norm}
                  onClick={() => { setStatus("active"); setQ(z.city); }}
                  data-testid={`zone-${z.city_norm}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 hover:border-amber-500"
                >
                  {z.city}
                  {z.postal_code ? <span className="text-amber-400">({z.postal_code})</span> : null}
                  <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[11px] font-bold text-white">{z.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="waitlist-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Élève</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Rayon</th>
                <th className="px-4 py-3">Inscrit le</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : (data.items || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-400">
                    {"Aucune inscription en liste d'attente."}
                  </td>
                </tr>
              ) : (
                data.items.map((w) => (
                  <tr key={w.id} data-testid={`waitlist-row-${w.id}`}>
                    <td className="px-4 py-3 font-semibold text-ink">{w.first_name || "—"}</td>
                    <td className="px-4 py-3">
                      <div>{w.email}</div>
                      {w.phone && <div className="text-xs text-neutral-400">{w.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold">{w.city}</span>
                      {w.postal_code && <span className="text-neutral-400"> ({w.postal_code})</span>}
                    </td>
                    <td className="px-4 py-3 text-neutral-500">
                      {w.radius_km ? `${w.radius_km} km` : "—"}
                      {w.lat == null && <span className="ml-1 text-[10px] text-amber-500" title="Non géolocalisé (ancienne entrée)">⚠</span>}
                    </td>
                    <td className="px-4 py-3 text-neutral-500">{fmt(w.created_at)}</td>
                    <td className="px-4 py-3">
                      {w.status === "notified" ? (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                          Notifiée {w.notified_at ? `· ${fmt(w.notified_at)}` : ""}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
                          En attente
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => notify(w.id)}
                          disabled={busy === w.id}
                          data-testid={`waitlist-notify-${w.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white hover:bg-brand/90 disabled:opacity-50"
                        >
                          {busy === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                          Prévenir
                        </button>
                        <button
                          onClick={() => remove(w.id)}
                          disabled={busy === w.id}
                          className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1.5 text-xs font-semibold text-neutral-500 hover:border-red-300 hover:text-red-500 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
